import React, { FC, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { formatWeightValue } from 'utils';
import { Co2FootprintVO } from 'vo';
import * as utils from 'utils';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';

interface ChartDonutSectionProps {
  co2FootprintPerAttendeVOs: {
    travelAndAccommodation: Co2FootprintVO;
    hospitality: Co2FootprintVO;
    catering: Co2FootprintVO;
    merchandise: Co2FootprintVO;
    eventDurationAndInfo: Co2FootprintVO;
  };
  className?: string;
}

export const ChartDonutSection: FC<ChartDonutSectionProps> = ({ co2FootprintPerAttendeVOs, className }) => {
  const { t } = useTranslation();

  const co2FootprintPerAttendeeVO = Co2FootprintVO.sum(Object.values(co2FootprintPerAttendeVOs));
  const resultPerAttendee = co2FootprintPerAttendeeVO.kg;

  const co2FootprintLevel = useMemo(() => {
    const co2FootprintKg = co2FootprintPerAttendeeVO.kg;
    if (co2FootprintKg <= 300) {
      return 'Low';
    }
    if (co2FootprintKg >= 1500) {
      return 'High';
    }
    return 'Average';
  }, [co2FootprintPerAttendeeVO.kg]);

  const co2footPrintLevelNames = {
    ['Low']: t('personal.pages.result.footprintLevel.low'),
    ['Average']: t('personal.pages.result.footprintLevel.average'),
    ['High']: t('personal.pages.result.footprintLevel.high'),
  };
  const results = [
    {
      markColor: 'var(--chart-color1)',
      text: t('events.pages.result.topContainer.marks.travelAndAccommodation'),
      value: co2FootprintPerAttendeVOs.travelAndAccommodation.stringifiedValue,
    },
    {
      markColor: 'var(--chart-color2)',
      text: t('events.pages.result.topContainer.marks.hospitality'),
      value: co2FootprintPerAttendeVOs.hospitality.stringifiedValue,
    },
    {
      markColor: 'var(--chart-color3)',
      text: t('events.pages.result.topContainer.marks.catering'),
      value: co2FootprintPerAttendeVOs.catering.stringifiedValue,
    },
    {
      markColor: 'var(--chart-color4)',
      text: t('events.pages.result.topContainer.marks.merchandise'),
      value: co2FootprintPerAttendeVOs.merchandise.stringifiedValue,
    },
    {
      markColor: 'var(--chart-color5)',
      text: t('events.pages.result.topContainer.marks.durationAndVenue'),
      value: co2FootprintPerAttendeVOs.eventDurationAndInfo.stringifiedValue,
    },
  ];
  const renderResults = () => {
    return results.map(({ markColor, text, value }) => (
      <ResultLine key={text}>
        <ResultTitle>
          <Mark $color={markColor} />
          {text}
        </ResultTitle>
        <ResultValue>{value}</ResultValue>
      </ResultLine>
    ));
  };

  return (
    <Root className={className}>
      <StyledChart
        options={{
          states: {
            hover: {
              filter: {
                type: 'none',
              },
            },
          },
          stroke: { width: 0 },
          colors: [
            'var(--chart-color1)',
            'var(--chart-color2)',
            'var(--chart-color3)',
            'var(--chart-color4)',
            'var(--chart-color5)',
          ],
          legend: {
            show: false,
          },
          tooltip: {
            enabled: true,
            y: {
              formatter: (value: number) => {
                return utils.formatWeightValue(value);
              },
            },
          },
          labels: [
            t('events.pages.result.topContainer.marks.travelAndAccommodation'),
            t('events.pages.result.topContainer.marks.hospitality'),
            t('events.pages.result.topContainer.marks.catering'),
            t('events.pages.result.topContainer.marks.merchandise'),
            t('events.pages.result.topContainer.marks.durationAndVenue'),
          ],
          dataLabels: {
            enabled: false,
          },
          plotOptions: {
            pie: {
              donut: {
                labels: {
                  show: true,
                  name: {
                    show: true,
                    fontFamily: 'Poppins',
                    color: 'var(--color6)',
                    offsetY: 1,
                    formatter: () => formatWeightValue(resultPerAttendee),
                  },
                  value: {
                    show: true,
                    fontFamily: 'Poppins',
                    color: 'var(--color6)',
                    offsetY: 10,
                    formatter: () => t('events.pages.result.topContainer.perAttendee'),
                  },
                  total: {
                    show: true,
                    fontFamily: 'Poppins',
                    color: 'var(--color6)',
                    label: formatWeightValue(resultPerAttendee),
                    formatter: () => t('events.pages.result.topContainer.perAttendee'),
                  },
                },
              },
            },
          },
        }}
        series={[
          co2FootprintPerAttendeVOs.travelAndAccommodation.formattedKg,
          co2FootprintPerAttendeVOs.hospitality.formattedKg,
          co2FootprintPerAttendeVOs.catering.formattedKg,
          co2FootprintPerAttendeVOs.merchandise.formattedKg,
          co2FootprintPerAttendeVOs.eventDurationAndInfo.formattedKg,
        ]}
        type="donut"
      />
      <Text>{t('events.pages.result.footprintDescription')}</Text>
      <FootprintLevel>
        <FootprintBadge $level={co2FootprintLevel}>{co2footPrintLevelNames[co2FootprintLevel]}</FootprintBadge>
      </FootprintLevel>
      <Divider />
      {renderResults()}
    </Root>
  );
};

const Root = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  padding: 28px 0 24px;
  border-radius: 20px;
  background: var(--color5);
  border: 1px solid var(--color10);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  margin: 0 auto;

  @media ${DEVICE.tablet} {
    min-width: 310px;
    max-width: 350px;
  }
  @media ${DEVICE.laptop} {
    margin: 0;
  }
`;

const Text = styled.div`
  margin-bottom: 8px;
  padding: 0 30px;
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color9);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const FootprintLevel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
`;

const FootprintBadge = styled.span<{ $level: string }>`
  ${TYPOGRAPHY.bodyMedium16}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 20px;
  border-radius: 50px;
  background: ${({ $level }) => ($level === 'Low' ? '#E8F5E9' : $level === 'Average' ? '#FFF3E0' : '#FFEBEE')};
  color: ${({ $level }) => ($level === 'Low' ? '#2E7D32' : $level === 'Average' ? '#E65100' : '#C62828')};
  font-weight: 600;

  &::before {
    content: '●';
    font-size: 10px;
  }
`;

const StyledChart = styled(Chart)`
  position: relative;
  margin-bottom: 17px;

  .apexcharts-datalabel-label {
    ${TYPOGRAPHY.titleSemibold24}
    color: var(--color6);
  }

  .apexcharts-datalabel-value {
    ${TYPOGRAPHY.bodyMedium16}
    fill: var(--color6);
  }
`;

const Divider = styled.div`
  width: calc(100% - 40px);
  height: 1px;
  background: var(--color10);
  margin: 0 auto 16px;
`;

const ResultLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 0 20px;
  &:last-child {
    margin-bottom: 0;
  }
  color: var(--color6);
`;

const Mark = styled.span<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background-color: ${({ $color }) => $color};
  margin-right: 8px;
  flex-shrink: 0;
`;

const ResultTitle = styled.div`
  display: flex;
  align-items: center;
  text-align: left;
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color6);

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyRegular14}
  }
`;

const ResultValue = styled.div`
  ${TYPOGRAPHY.captionMedium12}
  color: var(--color6);

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyMedium16}
  }
`;
